import type { UnitSystem } from '@/core/Units';
import terrainTemplatesData from '@/data/terrain-templates.json';
import type { TerrainGenerationParams, TerrainTemplateId } from './TerrainTypes';
import { TERRAIN_TEMPLATE_IDS } from './TerrainTypes';

/** Metadata + default generation parameters for a canvas template. */
export interface TerrainTemplate {
  id: TerrainTemplateId;
  name: string;
  description: string;
  defaultUnits: UnitSystem;
  defaults: Omit<TerrainGenerationParams, 'templateId'>;
}

interface TerrainTemplatesFile {
  schemaVersion: number;
  templates: TerrainTemplate[];
}

const file = terrainTemplatesData as TerrainTemplatesFile;

const TEMPLATES: ReadonlyMap<TerrainTemplateId, TerrainTemplate> = new Map(
  file.templates.map((t) => [t.id, t]),
);

/** All templates in declared order. */
export function listTerrainTemplates(): readonly TerrainTemplate[] {
  return file.templates;
}

/** Look up a template by id, or throw when unknown. */
export function getTerrainTemplate(id: TerrainTemplateId): TerrainTemplate {
  const template = TEMPLATES.get(id);
  if (!template) {
    throw new Error(`Unknown terrain template: ${id}`);
  }
  return template;
}

/** Verify every declared template id has data (used at startup/tests). */
export function validateTerrainTemplates(): void {
  for (const id of TERRAIN_TEMPLATE_IDS) {
    if (!TEMPLATES.has(id)) {
      throw new Error(`Missing terrain template data for: ${id}`);
    }
  }
}

/**
 * Build concrete generation parameters for a template, applying optional overrides. Overrides let
 * the Blank Custom Canvas honor user-specified width/length/seed/amplitude/roughness/north (spec
 * §6.1).
 */
export function createGenerationParams(
  id: TerrainTemplateId,
  overrides: Partial<Omit<TerrainGenerationParams, 'templateId'>> = {},
): TerrainGenerationParams {
  const template = getTerrainTemplate(id);
  return {
    templateId: id,
    ...template.defaults,
    ...overrides,
  };
}
